import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const fieldKeys = [
    'registration_date', 'family_name', 'given_name', 'family_name_kana',
    'given_name_kana', 'nationality', 'nationality_other', 'gender',
    'birth_date', 'age', 'postal_code', 'address',
    'address_detail', 'phone', 'email', 'contact_methods',
    'education', 'employment_status', 'transfer_timing', 'job_search_status',
    'preferred_location_1', 'preferred_location_2', 'preferred_location_3', 'desired_job_category',
    'desired_job', 'desired_job_other', 'current_salary', 'desired_salary',
    'employment_types', 'employment_other', 'experiences', 'language_skills',
    'pc_skills', 'pc_other', 'qualifications', 'transfer_reason',
    'self_pr', 'resume_later', 'privacy_consent',
];
const buttonClass = 'px-4 py-2 rounded-lg border border-[var(--border)] hover:bg-[var(--neutral-100)] disabled:opacity-50 disabled:cursor-not-allowed';
const hasValue = value => value !== null && value !== undefined && value !== '' && (!Array.isArray(value) || value.length > 0);
const text = value => Array.isArray(value) ? value.map(text).join('、') : (value && typeof value === 'object' ? JSON.stringify(value) : String(value ?? ''));
const formatDate = value => {
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime()) ? date.toLocaleString('ja-JP', {
        timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }) : '---';
};

function FieldValue({ field, value, t }) {
    const missing = t('jobSeekers.missing');
    if (field === 'experiences' && Array.isArray(value)) {
        return value.map((entry, index) => {
            const experience = entry || {};
            return <div key={index} className="mb-3 space-y-1">
                <div>{t('jobSeekers.company')}：{text(experience.company) || missing}</div>
                <div>{t('jobSeekers.period')}：{text(experience.start) || missing} ～ {experience.current ? t('jobSeekers.current') : text(experience.end) || missing}</div>
                <div>{t('jobSeekers.employment')}：{text(experience.employment_type) || missing}</div>
                <div>{t('jobSeekers.position')}：{text(experience.position) || missing}</div>
                <div>{t('jobSeekers.duties')}：{text(experience.duties) || missing}</div>
            </div>;
        });
    }
    if (field === 'language_skills' && Array.isArray(value)) {
        return value.map((entry, index) => <div key={index}>{[
            entry?.language === 'その他' ? entry.other_language : entry?.language,
            entry?.certificate, entry?.level,
        ].map(part => text(part) || missing).join(' ／ ')}</div>);
    }
    if (field === 'age') return t('jobSeekers.age', { count: Number(value) });
    if (field === 'privacy_consent' && value === 'agreed') return t('jobSeekers.agreed');
    if (field === 'resume_later' && value === 'yes') return t('jobSeekers.later');
    if (['current_salary', 'desired_salary'].includes(field) && typeof value === 'string' && /^\d+(\.\d+)?$/.test(value.trim())) {
        return t('jobSeekers.salary', { value: value.trim() });
    }
    return text(value);
}

export function JobSeekerActionCard({ onClick }) {
    const { t } = useTranslation();
    return <button type="button" onClick={onClick} className="action-card action-card--teal">
        <div className="action-card-icon"><svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H5v15h14V5h-4M9 3h6v4H9zM8 12h8M8 16h6" />
        </svg></div>
        <span className="action-card-title">{t('jobSeekers.title')}</span>
        <span className="action-card-subtitle">{t('jobSeekers.description')}</span>
    </button>;
}

export default function JobSeekerRegistrationsPanel() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const allowed = user?.role === 'Admin' || user?.role === 'SuperAdmin';
    const [page, setPage] = useState(1);
    const [reload, setReload] = useState(0);
    const [result, setResult] = useState({ items: [], total: 0, pageSize: 20 });
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [error, setError] = useState('');
    const [status, setStatus] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!allowed) return;
        let active = true;
        setLoading(true);
        setError('');
        setSelected(null);
        apiService.getJobSeekerRegistrations({ page, pageSize: 20 }).then(data => {
            if (active) setResult({ items: data.items || [], total: Number.isFinite(data.total) ? data.total : null, pageSize: data.pageSize || 20 });
        }).catch(err => { if (active) setError(err.message); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [allowed, page, reload]);

    useEffect(() => {
        setDetail(null);
        if (!allowed || !selected) return;
        let active = true;
        setDetailLoading(true);
        setError('');
        apiService.getJobSeekerRegistration(selected.id).then(data => { if (active) setDetail(data); })
            .catch(err => { if (active) setError(err.message); })
            .finally(() => { if (active) setDetailLoading(false); });
        return () => { active = false; };
    }, [allowed, selected]);

    if (!allowed) return null;

    const notification = value => t(`jobSeekers.${value === true ? 'delivered' : value === false ? 'failed' : 'unknown'}`);
    const download = async kind => {
        setBusy(true);
        setError('');
        try {
            const { blob, fileName } = await apiService.downloadJobSeekerAttachment(selected.id, kind);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            try {
                link.href = url;
                link.download = fileName || detail.attachments.find(a => a.kind === kind)?.name || kind;
                document.body.appendChild(link);
                link.click();
            } finally {
                link.remove();
                setTimeout(() => URL.revokeObjectURL(url), 0);
            }
        } catch (err) { setError(err.message); }
        finally { setBusy(false); }
    };
    const remove = async () => {
        if (!window.confirm(t('jobSeekers.confirm', { name: selected.fullName || detail?.fields?.full_name || t('jobSeekers.missing') }))) return;
        setBusy(true);
        setError('');
        setStatus('');
        try {
            await apiService.deleteJobSeekerRegistration(selected.id);
            setResult(previous => ({ ...previous, items: previous.items.filter(item => item.id !== selected.id), total: previous.total === null ? null : Math.max(0, previous.total - 1) }));
            setSelected(null);
            setStatus(t('jobSeekers.deleted'));
            if (result.items.length === 1 && page > 1) setPage(page - 1);
        } catch (err) { setError(err.message); }
        finally { setBusy(false); }
    };
    const fields = detail?.fields || {};
    const orderedKeys = [...fieldKeys, ...Object.keys(fields).filter(key => !fieldKeys.includes(key))];

    const detailPanel = selected && (
        <section aria-label={selected.fullName || t('jobSeekers.name')} className="p-4 md:p-6 bg-[var(--surface)] rounded-lg border border-[var(--border)] space-y-4">
            <div className="flex justify-between items-center gap-3"><h3 className="font-bold">{selected.fullName}</h3><button type="button" className={buttonClass} disabled={busy} onClick={() => setSelected(null)}>{t('close')}</button></div>
            {detailLoading && <p role="status">{t('loading')}</p>}
            {detail && <>
                <dl className="space-y-3">
                    <div><dt className="text-sm text-[var(--text-secondary)]">{t('jobSeekers.submittedAt')}</dt><dd>{formatDate(detail.submittedAt)}</dd></div>
                    {orderedKeys.filter(key => hasValue(fields[key])).map(key => <div key={key}>
                        <dt className="text-sm text-[var(--text-secondary)]">{fieldKeys.includes(key) ? t(`jobSeekers.fields.${key}`) : key}</dt>
                        <dd className="whitespace-pre-wrap break-words"><FieldValue field={key} value={fields[key]} t={t} /></dd>
                    </div>)}
                    <div><dt>{t('jobSeekers.notification')}</dt><dd>{notification(detail.notificationDelivered)}</dd></div>
                    <div><dt>{t('jobSeekers.receipt')}</dt><dd>{notification(detail.receiptDelivered)}</dd></div>
                </dl>
                <div className="flex flex-wrap gap-3">
                    {['resume', 'history'].filter(kind => detail.attachments?.some(file => file.kind === kind)).map(kind => <button type="button" key={kind} className={buttonClass} disabled={busy} onClick={() => download(kind)}>{t(`jobSeekers.${kind}`)}</button>)}
                    <button type="button" className={`${buttonClass} text-red-700`} disabled={busy} onClick={remove}>{t('jobSeekers.delete')}</button>
                </div>
            </>}
        </section>
    );

    return <section aria-label={t('jobSeekers.title')} className="space-y-4 text-[var(--text-primary)]">
        <div className="flex flex-wrap justify-between items-center gap-3">
            <h2 className="text-xl font-bold">{t('jobSeekers.title')}</h2>
            <button type="button" className={buttonClass} disabled={loading || busy} onClick={() => setReload(value => value + 1)}>{t('refresh')}</button>
        </div>
        <p className="p-4 rounded-lg bg-[var(--surface)] border border-[var(--border)] text-sm">{t('jobSeekers.notice')}</p>
        <div role="status">{status}</div>
        {error && <p role="alert" className="text-red-700">{error}</p>}
        {loading ? <p role="status">{t('loading')}</p> : <>
            {!result.items.length ? <p>{t('jobSeekers.empty')}</p> : <div className="overflow-x-auto bg-[var(--surface)] border border-[var(--border)] rounded-lg">
                <table className="w-full text-sm text-left">
                    <thead><tr>{['submittedAt', 'name', 'fields.nationality', 'fields.desired_job', 'fields.contact_methods', 'attachments', 'notification'].map(key => <th key={key} scope="col" className="p-3 whitespace-nowrap">{t(`jobSeekers.${key}`)}</th>)}</tr></thead>
                    <tbody>{result.items.map(item => <React.Fragment key={item.id}><tr className="border-t border-[var(--border)] hover:bg-[var(--neutral-100)] cursor-pointer" onClick={() => { if (!busy) setSelected(item); }}>
                        <td className="p-3 whitespace-nowrap">{formatDate(item.submittedAt)}</td>
                        <td className="p-3"><button type="button" className="text-[var(--primary-600)] underline whitespace-nowrap" disabled={busy} aria-expanded={selected?.id === item.id} onClick={event => { event.stopPropagation(); setSelected(item); }}>{item.fullName || t('jobSeekers.missing')}</button></td>
                        <td className="p-3">{text(item.nationality) || '---'}</td>
                        <td className="p-3">{[item.desiredJobCategory, item.desiredJob].filter(hasValue).map(text).join(' ／ ') || '---'}</td>
                        <td className="p-3">{text(item.contactMethods) || '---'}</td>
                        <td className="p-3">{t('jobSeekers.documentCount', { count: item.attachments?.length || 0 })}</td>
                        <td className="p-3 whitespace-nowrap">{notification(item.notificationDelivered)}</td>
                    </tr>
                        {selected?.id === item.id && <tr><td colSpan={7} className="p-3">{detailPanel}</td></tr>}
                    </React.Fragment>)}</tbody>
                </table>
            </div>}
            <div className="flex items-center justify-center gap-4">
                <button type="button" className={buttonClass} disabled={page <= 1 || busy} onClick={() => setPage(page - 1)}>{t('jobSeekers.previous')}</button>
                <span>{result.total === null ? page : `${page} / ${Math.max(1, Math.ceil(result.total / result.pageSize))}`}</span>
                <button type="button" className={buttonClass} disabled={busy || (result.total === null ? result.items.length !== result.pageSize : page * result.pageSize >= result.total)} onClick={() => setPage(page + 1)}>{t('jobSeekers.next')}</button>
            </div>
        </>}

    </section>;
}
